import DynamicFieldsModel from '@linc-technologies/linced-common/models/dynamic-field/base-model';
import type { Type } from '@warp-drive/core/types/symbols';
import { attr } from '@warp-drive/legacy/model';
import type { FragmentArray } from 'ember-data-model-fragments/array/fragment';
import { fragmentArray } from 'ember-data-model-fragments/attributes';
import type { EnumValue } from '@linc-technologies/linced-common/enums/base-enum';
import type Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import type Contact from 'linced-enrolments/models/enrolments/contact';
import type MedicalCondition from '@linc-technologies/linced-common/models/enrolments/medical-condition';

// Proto: enrolments.Application.
export default class Application extends DynamicFieldsModel {
  declare [Type]: 'enrolments/application';

  // schoolId filters Enrolment Applications based on the school's LINC-ED Tenant ID.
  // For example, "61f78993-90a9-4392-92dd-83a4368528b8".
  @attr('string', { defaultValue: '' })
  declare schoolId: string;

  // campusId filters Enrolment Applications based on the school's LINC-ED Campus ID.
  @attr('string', { defaultValue: '' })
  declare campusId: string;

  // countryCode is a ISO Alpha 2 country code as to which country the school resides.
  @attr('string', { defaultValue: '' })
  declare countryCode: string;

  // schoolCode is a school identifier as given by a state based educational body.
  @attr('string', { defaultValue: '' })
  declare schoolCode: string;

  // enrolmentYear is the year in which the application was handed in for.
  @attr('number', { defaultValue: 0 })
  declare enrolmentYear: number;

  // jurisdiction is the unique ID of the applicable educational jurisdictional authority.
  @attr('number', { defaultValue: 0 })
  declare jurisdiction: EnumValue<typeof Jurisdiction> | 0;

  // authorId contains the id of the person who authored the received enrolment application.
  @attr('string', { defaultValue: '' })
  declare authorId: string;

  // personId is the linked LINC-ED person account, once the student has been enrolled.
  @attr('string', { defaultValue: '' })
  declare personId: string;

  // contacts is the list of contacts (primary + emergency) associated with this application to enrol.
  @fragmentArray('enrolments/contact')
  declare contacts: FragmentArray<Contact>;

  // medicalContacts is the list of doctors/specialists associated with this application to enrol.
  @fragmentArray('enrolments/contact')
  declare medicalContacts: FragmentArray<Contact>;

  // medicalConditions is the list of medical conditions associated with this application to enrol.
  @fragmentArray('enrolments/medical-condition')
  declare medicalConditions: FragmentArray<MedicalCondition>;
}
